import { useCallback, useEffect, useRef, useState } from "react";

import { queryNewsRecommend } from "@api/news";

/**
 * 资讯详情的相似文章 / 最新内容推荐取数。
 *
 * 进入详情 / id 变化时请求 `/api/news/recommend?id=...`：
 * - success=true → similar / latest 取 result 中的数组（缺失按空数组）；
 * - success=false / result 缺失 → error=true，errorInfo 带业务错误码；
 * - 网络失败 → error=true，errorInfo="network"；
 * - 切详情 / 卸载时丢弃在途响应。
 *
 * 当前页面只渲染 similar；latest 已取到，等设计稿再接。
 */
export default function useNewsRecommend(id) {
  const [similar, setSimilar] = useState([]);
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState(false);
  const [errorInfo, setErrorInfo] = useState("");
  const requestSeqRef = useRef(0);

  const load = useCallback(() => {
    if (!id) {
      setSimilar([]);
      setLatest([]);
      setLoading(false);
      setError(false);
      setErrorInfo("");
      return;
    }

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setSimilar([]);
    setLatest([]);
    setLoading(true);
    setError(false);
    setErrorInfo("");

    queryNewsRecommend(id)
      .then((res) => {
        if (seq !== requestSeqRef.current) return;

        if (!res?.success || !res.result) {
          setSimilar([]);
          setLatest([]);
          setError(true);
          setErrorInfo(res?.info || res?.error || "");
          return;
        }

        setSimilar(Array.isArray(res.result.similar) ? res.result.similar : []);
        setLatest(Array.isArray(res.result.latest) ? res.result.latest : []);
        setError(false);
      })
      .catch(() => {
        if (seq !== requestSeqRef.current) return;
        setSimilar([]);
        setLatest([]);
        setError(true);
        setErrorInfo("network");
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    load();

    return () => {
      requestSeqRef.current += 1;
    };
  }, [load]);

  return { similar, latest, loading, error, errorInfo, retry: load };
}
